import { getToken } from "./auth";

interface WebSocketOptions {
  url: string;
  onMessage?: (data: any) => void;
  onError?: (error: Event) => void;
  onClose?: () => void;
  onOpen?: () => void;
}

export class WebSocketClient {
  private ws: WebSocket | null = null;
  private options: WebSocketOptions;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectTimeout = 3000;
  private heartbeatTimer: any = null;
  private isDestroyed = false;

  constructor(options: WebSocketOptions) {
    this.options = options;
    this.connect();
  }

  private connect() {
    if (this.isDestroyed) return;

    try {
      // 确保在重连前关闭旧的连接
      if (this.ws) {
        this.ws.close();
        this.ws = null;
      }

      this.ws = new WebSocket(this.options.url);

      this.ws.onopen = () => {
        console.log("WebSocket connected");
        this.reconnectAttempts = 0;
        this.options.onOpen?.();
        this.subscribe();
        this.startHeartbeat();
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.options.onMessage?.(data);
        } catch (error) {
          console.error("Failed to parse WebSocket message:", error);
        }
      };

      this.ws.onerror = (error) => {
        console.error("WebSocket error:", error);
        this.options.onError?.(error);
      };

      this.ws.onclose = () => {
        console.log("WebSocket closed");
        this.stopHeartbeat();
        this.options.onClose?.();
        this.reconnect();
      };
    } catch (error) {
      console.error("WebSocket connection error:", error);
      this.reconnect();
    }
  }

  private reconnect() {
    if (
      this.isDestroyed ||
      this.reconnectAttempts >= this.maxReconnectAttempts
    ) {
      console.log("Max reconnection attempts reached or client destroyed");
      return;
    }

    this.stopHeartbeat(); // 重连前停止心跳

    this.reconnectAttempts++;
    console.log(`Reconnecting... Attempt ${this.reconnectAttempts}`);

    setTimeout(() => {
      this.connect();
    }, this.reconnectTimeout);
  }

  private startHeartbeat() {
    this.stopHeartbeat(); // 先清除可能存在的心跳定时器

    let lastMessageTime = Date.now();

    this.heartbeatTimer = setInterval(() => {
      // 如果距离上次消息超过3秒，说明可能断开了
      if (Date.now() - lastMessageTime > 3000) {
        console.log("Connection may be lost, attempting to reconnect...");
        this.reconnect();
        return;
      }

      if (this.ws?.readyState === WebSocket.OPEN) {
        this.subscribe(); // 发送心跳包
        lastMessageTime = Date.now();
      }
    }, 500); // 500ms一次心跳
  }

  private stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private subscribe() {
    const subscribeMessage = {
      event: "sub.bizhong@list",
      data: { token: getToken() || "" },
    };
    this.send(JSON.stringify(subscribeMessage));
  }

  public send(data: string) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(data);
    } else {
      console.error("WebSocket is not connected");
    }
  }

  public close() {
    this.isDestroyed = true;
    this.stopHeartbeat();
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }
}

export function getWsUrl() {
  if (process.env.NODE_ENV === "production") {
    const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    return `${wsProtocol}//api.${window.location.host}/websocket`;
  }
  return "wss://api.madgain.shop/websocket";
}

// 创建WebSocket实例
export const createWebSocket = (onMessage?: (data: any) => void) => {
  return new WebSocketClient({
    url: getWsUrl(),
    onMessage,
    onError: (error) => {
      console.error("WebSocket error:", error);
    },
    onClose: () => {
      console.log("WebSocket connection closed");
    },
    onOpen: () => {
      console.log("WebSocket connection opened");
    },
  });
};

export default createWebSocket;
